import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { ToastrService } from 'ngx-toastr';
import { concat, defer, Observable, of, Subscription } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { faL } from '@fortawesome/free-solid-svg-icons';

export enum VerificationType {
  workerId = 'Worker ID',
  supplierId = 'supplier id',
}

type FormControls = {
  verification: FormControl<VerificationType | null>;
  workerId?: FormControl<string | null>;
  supplierId?: FormControl<string | null>;
};

type FormValues = {
  verification: VerificationType | null;
  workerId?: string | null;
  supplierId?: string | null;
};

@Component({
  selector: 'ciciam-form-forgot-username',
  templateUrl: './form-forgot-username.component.html',
  styleUrls: ['./form-forgot-username.component.scss'],
})
export class FormForgotUsernameComponent {
  @Input() isLoading: boolean = false;
  @Output() forgotUsername = new EventEmitter<{
    verificationType: VerificationType;
    id: string;
    recaptchaV2Token: string;
    recaptchaV3Token: string;
  }>();

  verificationValueChanges?: Observable<VerificationType | null>;

  buttonGroupClasses = {
    selected: [
      'z-10',
      'ring-2',
      'ring-notus-primary-300',
      'bg-notus-primary-700',
      'text-white',
    ],
  };

  verificationTypes: VerificationType[] = Object.values(VerificationType);

  form = this.formBuilder.nonNullable.group<FormControls>(
    {
      verification: new FormControl(VerificationType.workerId),
    }
    // {
    //   validators: [
    //     (group: AbstractControl) => {
    //       const _group = group as FormGroup;
    //       const errors = Object.keys(_group.controls)
    //         .map((key) => {
    //           const control = _group.get(key);

    //           const errors = control?.validator?.(control) ?? null;
    //           control?.setErrors(errors);
    //           if (control) {
    //             console.log(
    //               `validators; ${key} - setErrors: ${JSON.stringify(errors)}`
    //             );
    //           }

    //           return errors;
    //         })
    //         .filter((errors) => !!errors);

    //       console.log(
    //         `validators; aggregate errors: ${JSON.stringify(errors)}`
    //       );

    //       return errors;
    //     },
    //   ],
    // }
  );

  verificationControlSubscription?: Subscription;
  recaptchaV3Token: string = '';
  recaptchaV3TokenVerified: boolean = false;
  recaptchaV2: boolean = false;
  recaptchaV2Token: string = '';

  get verifyWorkerId(): boolean {
    return this.form.controls.verification.value === VerificationType.workerId;
  }

  get verifySupplierId(): boolean {
    return (
      this.form.controls.verification.value === VerificationType.supplierId
    );
  }

  get formValues(): FormValues {
    return this.form.getRawValue();
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private supportService: SupportService,
    private toastr: ToastrService,
    private recaptchaV3Service: ReCaptchaV3Service // Added reCAPTCHA service
  ) {}

  ngOnInit(): void {
    const verificationControl = this.form.controls.verification;

    if (verificationControl) {
      this.verificationValueChanges = concat(
        defer(() => of(this.form.controls.verification?.value ?? null)),
        this.form.controls.verification?.valueChanges
      );

      this.verificationControlSubscription =
        this.verificationValueChanges.subscribe((_type) => {
          console.log(`verification type: ${_type}`);
          this.onVerificationControlValueChanges(_type);
        });
    }

    this.recaptchaV3Service.execute('submit').subscribe({next: async (token) => {
      this.recaptchaV3Token = token;
      try {
        let validationResult = await this.supportService.recaptchaV3Validate(token);
        this.recaptchaV3TokenVerified = validationResult.success;
        if(!validationResult.success) {
          // enable recaptcha V2 if recaptcha V3 fails
          this.recaptchaV2 = true;
        }
      } catch (error) {
        // enable recaptcha V2 if recaptcha V3 fails
        this.recaptchaV2 = true
      }
    }, error: (error) => {
      // enable recaptcha V2 if recaptcha V3 fails
      this.recaptchaV2 = true;
    }});
  }

  resetForm() {
    this.form.reset();
  }

  verificationButtonClasses(type: VerificationType, index: number): string[] {
    let _classes: string[] = [];

    if (index == 0) {
      _classes = ['rounded-s-full', 'border'];
    } else if (index === this.verificationTypes.length - 1) {
      _classes = ['rounded-e-full', 'border'];
    } else {
      _classes = ['border-y'];
    }

    if (this.form.controls.verification.value === type) {
      _classes = _classes.concat(this.buttonGroupClasses.selected);
    }

    return _classes;
  }

  onRecaptchaV2Resolve(token: string) {
    this.recaptchaV2Token = token;
  }

  async onSubmit() {
    const verificationValue = this.formValues.verification;

    if(this.recaptchaV3TokenVerified) {
      let token = await this.recaptchaV3Service.execute('submit').toPromise();
      if(token) {
        this.recaptchaV3Token = token;
      } else {
        this.recaptchaV3TokenVerified = false;
        this.recaptchaV2 = true;
      }
    }

    if(!this.recaptchaV3TokenVerified && !this.recaptchaV2) {
      // enable recaptcha V2 if recaptcha V3 fails or not ready yet
      this.recaptchaV2 = true;
      return;
    }

    if(this.recaptchaV2 && !this.recaptchaV2Token) {
      this.toastr.error(this.translate.instant('Please complete the reCAPTCHA.'));
      return;
    }

    if (this.form.valid && verificationValue) {
      this.forgotUsername.emit({
        verificationType: verificationValue,
        id:
          verificationValue == VerificationType.workerId
            ? this.formValues.workerId!
            : this.formValues.supplierId!,
        recaptchaV2Token: this.recaptchaV2 ? this.recaptchaV2Token : '',
        recaptchaV3Token: this.recaptchaV2 ? '' : this.recaptchaV3Token,
      });
    } else {
      this.form.updateValueAndValidity();
      this.form.markAllAsTouched();
    }
  }

  private onVerificationControlValueChanges(
    value: VerificationType | null
  ): void {
    switch (value) {
      case VerificationType.workerId:
        this.form.setControl(
          'workerId',
          new FormControl('', [Validators.required])
        );
        if (this.form.controls.supplierId) {
          this.form.removeControl('supplierId');
        }
        break;
      case VerificationType.supplierId:
        this.form.setControl(
          'supplierId',
          new FormControl('', [Validators.required])
        );
        if (this.form.controls.workerId) {
          this.form.removeControl('workerId');
        }
        break;
      default:
        break;
    }
  }
}
